"use client";

import type React from "react";

import Arrow from "@homeinstead/icons/Arrow";
import Close from "@homeinstead/icons/Close";
import { cn } from "@homeinstead/ui";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerOverlay,
  DrawerTitle,
} from "@homeinstead/ui/components/Drawer";
import { ScrollArea } from "@homeinstead/ui/components/ScrollArea";
import { useMediaQuery } from "usehooks-ts";

const handleDrawerClose = ({
  searchParams,
  pathname,
}: {
  searchParams: URLSearchParams;
  pathname: string;
}) => {
  const params = new URLSearchParams(searchParams.toString());
  params.delete("leadVariant");

  const queryString = params.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
};

const LeadFormLayoutHeader = ({
  hasBackButton,
  onBack,
}: {
  hasBackButton: boolean;
  onBack?: () => void;
}) => (
  <div
    className={`flex h-20 px-8 py-4 ${hasBackButton ? "justify-between" : "justify-end"}`}
  >
    {hasBackButton && (
      <button
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-full hover:bg-new-beginnings-tints-800 focus:outline focus:ring-2 focus:ring-new-beginnings-base focus:ring-offset-4",
        )}
        onClick={onBack}
        type="button"
      >
        <Arrow className="size-4 rotate-90 fill-new-beginnings-base stroke-[2px] stroke-new-beginnings-base" />
      </button>
    )}
    <DrawerClose asChild>
      <button className="inline-flex h-10 items-center justify-end gap-2 font-bold text-new-beginnings-base">
        Close <Close className="mt-1 size-4" />
      </button>
    </DrawerClose>
  </div>
);

interface LeadFormDrawerProps {
  isDrawerOpen: boolean;
  hasBackButton?: boolean;
  onBack?: () => void;
  onClose?: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

const LeadFormDrawer: React.FC<LeadFormDrawerProps> = ({
  isDrawerOpen,
  hasBackButton = true,
  onBack,
  onClose,
  title = "Submit an Inquiry",
  description = "Submit an inquiry to be a caregiver or get care for a loved one.",
  children,
  className,
}) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  return (
    <Drawer
      direction={isDesktop ? "right" : "bottom"}
      onClose={onClose}
      open={isDrawerOpen}
    >
      <DrawerOverlay />
      <DrawerContent className={cn("bg-support-tints-400 md:left-auto")}>
        <ScrollArea>
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
          <DrawerDescription className="sr-only">
            {description}
          </DrawerDescription>
          <LeadFormLayoutHeader hasBackButton={hasBackButton} onBack={onBack} />
          <hr className="border-new-beginnings-base/25" />
          <div className={cn("px-4 xs:px-12 py-4", className)}>{children}</div>
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
};

export { handleDrawerClose, LeadFormDrawer };
